/**
 * 전자책 페이지 비율(가로:세로) — 클라/관리자 공유 단일 정의(CLAUDE.md).
 * 값은 CSS `aspect-ratio` 그대로 쓰는 "가로/세로" 문자열(예 "16/9"). 미설정 시 기본 세로 3/4.
 */

export const DEFAULT_ASPECT = "3/4";

export interface AspectPreset {
  id: string;
  label: string; // 운영자용 쉬운 이름
  value: string; // "가로/세로"
}

// 그림책·디지털에서 흔한 비율 조사 결과(세로→정사각→가로 순).
export const ASPECT_PRESETS: readonly AspectPreset[] = [
  { id: "p34", label: "세로 기본 (3:4)", value: "3/4" },
  { id: "p23", label: "긴 세로 (2:3)", value: "2/3" },
  { id: "p916", label: "폰 세로 (9:16)", value: "9/16" },
  { id: "a4", label: "A4 세로", value: "210/297" },
  { id: "sq", label: "정사각 (1:1)", value: "1/1" },
  { id: "l43", label: "가로 (4:3)", value: "4/3" },
  { id: "l32", label: "가로 사진 (3:2)", value: "3/2" },
  { id: "l169", label: "와이드 가로 (16:9)", value: "16/9" },
];

const MIN_RATIO = 0.2; // 세로로 너무 길쭉(1:5)한 극단 방지
const MAX_RATIO = 5; // 가로로 너무 길쭉(5:1) 방지

/** "W/H"(또는 "W:H") 검증·정규화. 유효하지 않으면 undefined(→ 호출부가 기본값). */
export function normalizeAspect(input: unknown): string | undefined {
  if (typeof input !== "string") return undefined;
  const m = /^\s*(\d+(?:\.\d+)?)\s*[/:]\s*(\d+(?:\.\d+)?)\s*$/.exec(input);
  if (!m) return undefined;
  const w = Number(m[1]);
  const h = Number(m[2]);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return undefined;
  const ratio = w / h;
  if (ratio < MIN_RATIO || ratio > MAX_RATIO) return undefined;
  // 정수면 정수로, 소수면 소수 유지. 공백 제거한 "W/H".
  return `${trimNum(w)}/${trimNum(h)}`;
}

/** 프리셋에 있으면 라벨, 없으면 "가로:세로 (직접)". */
export function aspectLabel(value: string | undefined): string {
  const v = value ?? DEFAULT_ASPECT;
  const preset = ASPECT_PRESETS.find((p) => p.value === v);
  if (preset) return preset.label;
  const [w, h] = v.split("/");
  return `${w}:${h} (직접)`;
}

function trimNum(n: number): string {
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
}

/**
 * 📐 **가로 ÷ 세로 를 숫자로** — CSS 가 「폭과 높이 중 작은 쪽」에 맞춰 줄일 때 쓴다.
 *
 * ## 🔴 왜 숫자가 따로 필요한가 (2026-09-10 · 폰에서 그림이 잘리고 있었다)
 * `aspect-ratio: 3/4` 만으로는 **폭도 높이도 꽉 찬** 상자를 못 만든다. 작은 폰에서
 * `max-height` 가 상자를 잘라도 `aspect-ratio` 는 여전히 옛 높이를 기준으로 삼아,
 * 그 안의 그림이 상자 밖으로 **60px 넘쳐 잘려 나갔다**(457 중 13%). 《나무 위 도서관》의
 * 종이 고래는 천장의 어둠과 책 아랫부분을 잃었다. 큰 폰에서는 안 나서 더 늦게 찾았다.
 *
 * 그래서 CSS 가 `width: min(100%, 100cqh * var(--page-ar))` 로 **직접 고른다.**
 * 그러려면 비율이 `3/4` 같은 글자가 아니라 **0.75 라는 수**여야 한다.
 *
 * 🔴 상자가 그림에 딱 맞아야 하는 더 큰 이유: 마법의 자리(0~1)는 **이 상자 기준**이다.
 *    상자가 그림보다 크면 누르는 자리가 통째로 어긋난다.
 */
export function aspectRatioNumber(input: unknown): number {
  const v = normalizeAspect(input) ?? DEFAULT_ASPECT;
  const [w, h] = v.split("/").map(Number);
  return w && h ? w / h : 0.75;
}
